import React, { useState, useEffect } from "react";

/**
 * InAppBrowser — fullscreen modal that loads a 3rd-party URL inside an
 * iframe so the owner stays in-context instead of being kicked to a new
 * tab.  Many sites (Gmail, Ring, GitHub) block iframe embedding via the
 * `X-Frame-Options: DENY` or `Content-Security-Policy: frame-ancestors`
 * headers — when that happens the iframe is blank, so we surface a
 * prominent "Open in New Tab" button as the escape hatch.
 *
 * Trigger: dispatched via `window.dispatchEvent('rawco-inapp-browser', {detail: {url, title}})`
 * which App.js converts every open_gmail / open_ring / open_github etc.
 * into instead of `window.open()`.
 */
export default function InAppBrowser({ url, title, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // If the iframe hasn't fired `load` within 2s assume it's been blocked
  // by X-Frame-Options and show the fallback CTA. Was 6s — owner reported
  // "pull up Snapdog dashboard doesn't work" which was actually 6 seconds
  // of black screen before the new-tab fallback appeared. Most sister
  // apps now open in a new tab directly via the backend's `new_tab` flag.
  useEffect(() => {
    if (!url) return;
    const t = setTimeout(() => { if (!loaded) setErrored(true); }, 2000);
    return () => clearTimeout(t);
  }, [url, loaded]);

  if (!url) return null;
  return (
    <div
      data-testid="inapp-browser-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 95,
        background: "rgba(0, 4, 12, 0.85)",
        backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "5vh 4vw",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="inapp-browser"
        style={{
          width: "100%", height: "100%", maxWidth: 1400, maxHeight: "92vh",
          background: "#000",
          border: "1px solid var(--rc-gold)",
          borderRadius: 8,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 0 40px rgba(212, 175, 55, 0.3)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", borderBottom: "1px solid var(--rc-gold-dim)",
          background: "rgba(0, 4, 12, 0.96)",
        }}>
          <span style={{ color: "var(--rc-gold)", fontFamily: "var(--rc-font-display)", fontSize: 12, letterSpacing: 2 }}>
            // {title || "EMBEDDED VIEWER"}
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            <a
              href={url} target="_blank" rel="noopener noreferrer"
              data-testid="inapp-open-newtab"
              style={{
                padding: "8px 12px", fontSize: 11, letterSpacing: 1,
                border: "1px solid var(--rc-gold-dim)", color: "var(--rc-gold)",
                textDecoration: "none", borderRadius: 4,
              }}
            >↗ NEW TAB</a>
            <button
              data-testid="inapp-close"
              onClick={onClose}
              style={{
                width: 36, height: 36, border: "1px solid var(--rc-gold)",
                background: "transparent", color: "var(--rc-gold)",
                fontSize: 18, cursor: "pointer", borderRadius: 4,
              }}
            >×</button>
          </span>
        </div>
        <div style={{ flex: 1, position: "relative", background: "#000" }}>
          <iframe
            src={url}
            title={title || "viewer"}
            data-testid="inapp-iframe"
            onLoad={() => setLoaded(true)}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer-when-downgrade"
            style={{
              width: "100%", height: "100%", border: "none",
              background: "#000",
            }}
          />
          {errored && !loaded && (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 18, padding: 40, textAlign: "center",
              background: "rgba(0, 4, 12, 0.96)",
              color: "var(--rc-text)",
            }} data-testid="inapp-blocked">
              <div style={{ fontFamily: "var(--rc-font-display)", color: "var(--rc-gold)", fontSize: 16, letterSpacing: 2 }}>
                // EMBED BLOCKED BY SITE
              </div>
              <div style={{ fontSize: 13, color: "var(--rc-text-dim)", maxWidth: 480, lineHeight: 1.6 }}>
                {title || "This site"} doesn't allow itself to be loaded inside another app
                (security policy). Tap below to open it in a new tab — R.A.W. stays running here.
              </div>
              <a
                href={url} target="_blank" rel="noopener noreferrer"
                data-testid="inapp-fallback-open"
                style={{
                  padding: "12px 24px",
                  background: "var(--rc-gold)", color: "#000",
                  fontFamily: "var(--rc-font-display)", letterSpacing: 2, fontSize: 12,
                  textDecoration: "none", borderRadius: 4, fontWeight: 700,
                }}
              >→ OPEN IN NEW TAB</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
